import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Download, Upload, Eye, Undo2, AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { InfoTip } from '../InfoTip';
import { apiErrorMessage } from '../../pages/admin/transactions/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => {
  const n = Number(v) || 0;
  return `R ${n < 0 ? '-' : ''}${Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Add deal purchases / repayments since the last LB/RP workbook upload,
 * add-only (backend: routers/loan_cashflow_upload.py). Plus the one-time
 * backfill for New Deals / Repayments recorded in the portal before they
 * were wired into Reconciliation.
 */
export function LoanCashflowUploadCard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapseRepeats, setCollapseRepeats] = useState(false);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [skipErrorRows, setSkipErrorRows] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [backfill, setBackfill] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);

  const loadUploads = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/loan-cashflows/uploads`, { headers });
      setUploads(r.data.uploads || []);
    } catch { /* non-critical */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { loadUploads(); }, [loadUploads]);

  const send = async (dryRun) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/admin/loan-cashflows/upload?dry_run=${dryRun}&collapse_repeats=${collapseRepeats}`
        + (!dryRun && skipErrorRows ? '&skip_error_rows=true' : ''), fd, { headers });
      if (dryRun) {
        setPreview(r.data);
        setErrorsOnly(false);
        setSkipErrorRows(false);
      } else {
        const nl = r.data.summary.loans_created ? `, created ${r.data.summary.loans_created} loan(s)` : '';
        toast.success(`Added ${r.data.summary.cashflows_added} cashflow(s)${nl}`
          + (r.data.summary.columns_corrected ? `, corrected the column on ${r.data.summary.columns_corrected}` : ''));
        setPreview(null);
        setFile(null);
        loadUploads();
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, dryRun ? 'Preview failed' : 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    const r = await fetch(`${API_URL}/api/admin/loan-cashflows/template.csv`, { headers });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'loan_cashflows_template.csv';
    a.click();
  };

  // Undo: preview first. Nothing reconciled → plain confirm. Otherwise show
  // exactly which cashflows are matched (and to which bank line) and offer
  // "Undo and release matches".
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoBusy, setUndoBusy] = useState(false);

  const runUndo = async (u, releaseMatches) => {
    setUndoBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/loan-cashflows/uploads/${u.id}/reverse?release_matches=${releaseMatches}`, {}, { headers });
      const extra = [
        r.data.loans_removed ? `${r.data.loans_removed} loan(s) removed` : null,
        r.data.matches_released ? `${r.data.matches_released} match(es) released` : null,
      ].filter(Boolean).join(', ');
      toast.success(`Removed ${r.data.removed} cashflow(s)${extra ? ` — ${extra}` : ''}`);
      setUndoPreview(null);
      loadUploads();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Undo failed'));
    } finally {
      setUndoBusy(false);
    }
  };

  const undo = async (u) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/loan-cashflows/uploads/${u.id}/reverse-preview`, { headers });
      if (!r.data.reconciled?.length) {
        const loans = r.data.loans_created?.length ? ` and the ${r.data.loans_created.length} loan(s) it created` : '';
        if (window.confirm(`Undo upload "${u.filename}"? This removes the ${r.data.cashflows} cashflow(s) it added${loans}.`)) {
          await runUndo(u, false);
        }
        return;
      }
      setUndoPreview({ upload: u, ...r.data });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not check this upload'));
    }
  };

  const checkBackfill = async () => {
    setBackfillBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/loan-cashflows/backfill-portal`, { headers });
      setBackfill(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Check failed'));
    } finally {
      setBackfillBusy(false);
    }
  };
  const runBackfill = async () => {
    if (!window.confirm(`Add ${backfill?.to_create} missing cashflow(s) to Reconciliation?`)) return;
    setBackfillBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/loan-cashflows/backfill-portal`, {}, { headers });
      toast.success(`Added ${r.data.created} cashflow(s) to Reconciliation`);
      setBackfill(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Backfill failed'));
    } finally {
      setBackfillBusy(false);
    }
  };

  const s = preview?.summary;
  const errorRows = (preview?.rows || []).filter(r => r.errors?.length);
  const canCommit = s && (s.rows_with_errors === 0 || skipErrorRows) && (s.cashflows_to_add > 0 || s.column_corrections > 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Upload New Loan Cashflows
            <InfoTip>
              Adds deal purchases and repayments without re-uploading the whole LB/RP workbook. It only ever
              <strong>adds</strong> — nothing already in the system is changed, so existing allocations and matches
              are untouched. Each cashflow goes on the loan (balances, statements, GL) and into Reconciliation.
              <br /><br />
              <strong>New loans:</strong> for a loan that isn't in the system yet, fill in client_id, repurchase_date
              and interest_rate on its purchase row (rp_rate, deal_type and ltv optional). That row creates the loan
              — its date is the purchase date. Rates can be 0.24 or 24. Blank rp_rate uses the investor's standard
              rate. Later rows for the same loan (e.g. a repayment) attach to it.
            </InfoTip>
          </CardTitle>
          <CardDescription>
            Columns: loan_id, risk_participant, date, lb_amount, rp_amount — positive = purchase, negative = repayment.
            <strong>cashflow_column</strong>: the workbook column the cashflow is in (CashflowValue, CashflowValue1, …), required for a
            new loan's draw. A same-day recycle's draw is in CashflowValue1. For new loans also: client_id, repurchase_date,
            interest_rate (rp_rate, deal_type, ltv optional). <strong>loan_type</strong> (last column): New Loan or Same Day Recycle,
            checked against cashflow_column (New Loan → CashflowValue, Same Day Recycle → CashflowValue1); a mismatch blocks the upload.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="loan-cf-template-btn">
              <Download className="h-4 w-4 mr-1" /> Template
            </Button>
            <Input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="sm:max-w-sm"
              data-testid="loan-cf-file"
            />
            <Button size="sm" variant="outline" onClick={() => send(true)} disabled={!file || busy} data-testid="loan-cf-preview-btn">
              {busy && !preview ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />} Dry-Run Preview
            </Button>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={collapseRepeats}
              onChange={e => { setCollapseRepeats(e.target.checked); setPreview(null); }}
              data-testid="loan-cf-collapse-repeats"
            />
            <span>
              Identical rows are the <strong>same</strong> payment — don't add repeats.
              <span className="block text-xs text-slate-500">
                Leave unticked if a loan can genuinely have two identical payments on the same day (each extra identical row is then added).
                Changing this clears the preview — run Dry-Run again.
              </span>
            </span>
          </label>

          {preview && (
            <div className="space-y-3" data-testid="loan-cf-preview">
              <div className="flex flex-wrap gap-2 text-sm">
                <Badge className="bg-green-100 text-green-800">{s.cashflows_to_add} to add</Badge>
                {s.new_loans > 0 && <Badge className="bg-blue-100 text-blue-800">{s.new_loans} new loan(s) will be created</Badge>}
                <Badge variant="outline">{s.purchases_to_add} purchase(s) · {s.repayments_to_add} repayment(s)</Badge>
                <Badge variant="outline">{s.already_present} already present (skipped)</Badge>
                {s.column_corrections > 0 && (
                  <Badge className="bg-violet-100 text-violet-800" data-testid="loan-cf-column-fixes">
                    {s.column_corrections} column correction(s) to earlier uploaded cashflows
                  </Badge>
                )}
                {s.repeat_groups > 0 && (
                  <Badge className="bg-amber-100 text-amber-800">
                    {s.repeat_groups} repeated identical cashflow(s) — {s.collapse_repeats ? 'treated as one payment' : 'extras added as separate payments'}
                  </Badge>
                )}
                {s.rows_with_errors > 0 && (
                  <button type="button" onClick={() => setErrorsOnly(v => !v)} title="Show only the rows with errors" data-testid="loan-cf-errors-filter">
                    <Badge variant="destructive" className="cursor-pointer">
                      {s.rows_with_errors} row(s) with errors{errorsOnly ? ' (showing only these — click to show all)' : ' — click to show only these'}
                    </Badge>
                  </button>
                )}
              </div>
              {errorRows.length > 0 && (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm space-y-1" data-testid="loan-cf-error-list">
                  <p className="font-medium text-red-800 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> Fix these rows in your file (row = the line number in the spreadsheet), then preview again:
                  </p>
                  <ul className="max-h-40 overflow-y-auto text-red-800 text-xs space-y-0.5">
                    {errorRows.map(r => (
                      <li key={r.row}><strong>Row {r.row}</strong>{r.loan_id && r.loan_id !== 'nan' ? ` · ${r.loan_id}` : ''}: {r.errors.join('; ')}</li>
                    ))}
                  </ul>
                  <label className="flex items-start gap-2 pt-1 text-red-900">
                    <input type="checkbox" className="mt-0.5" checked={skipErrorRows} onChange={e => setSkipErrorRows(e.target.checked)} data-testid="loan-cf-skip-errors" />
                    <span>Skip these {errorRows.length} row(s) and commit the rest of the file. They won't be uploaded; fix and upload them separately.</span>
                  </label>
                </div>
              )}
              <div className="border rounded-lg overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left">
                      <th className="p-2">Row</th><th className="p-2">Loan</th><th className="p-2">RP</th>
                      <th className="p-2">Date</th><th className="p-2 text-right">LB</th><th className="p-2 text-right">RP</th>
                      <th className="p-2">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(errorsOnly ? errorRows : preview.rows).map(r => (
                      <tr key={r.row} className={`border-t ${r.errors?.length ? 'bg-red-50' : ''}`}>
                        <td className="p-2">{r.row}</td>
                        <td className="p-2 font-mono text-xs whitespace-nowrap">{r.loan_id}</td>
                        <td className="p-2">{r.risk_participant}</td>
                        <td className="p-2 whitespace-nowrap">{r.date}</td>
                        <td className="p-2 text-right font-mono whitespace-nowrap">{r.lb_amount ? fmt(r.lb_amount) : '—'}</td>
                        <td className="p-2 text-right font-mono whitespace-nowrap">{r.rp_amount ? fmt(r.rp_amount) : '—'}</td>
                        <td className="p-2 text-xs">
                          {!r.errors?.length && r.new_loan && (
                            <div className="text-blue-700 font-medium">
                              Creates loan · {r.new_loan.client_id} · to {r.new_loan.repurchase_date} ·{' '}
                              {(r.new_loan.interest_rate * 100).toFixed(2)}% / RP {(r.new_loan.rp_rate * 100).toFixed(2)}%
                              {r.new_loan.deal_type !== 'SRA' ? ` · ${r.new_loan.deal_type}` : ''}
                              {r.cashflow_column ? ` · draw in ${r.cashflow_column}` : ''}
                              {r.loan_type ? ` · ${r.loan_type}` : ''}
                            </div>
                          )}
                          {r.errors?.length
                            ? <span className="text-red-700">{r.errors.join('; ')}</span>
                            : r.legs.map(l => (
                                <div key={l.side}>
                                  {l.side}: {l.status === 'new'
                                    ? <span className="text-green-700">add</span>
                                    : <span className="text-slate-500">already present</span>}
                                  {l.column_fix && <span className="text-violet-700"> · column {l.column_fix}</span>}
                                  {l.note && <div className="text-amber-700">{l.note}</div>}
                                </div>
                              ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                onClick={() => send(false)}
                disabled={!canCommit || busy}
                className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
                data-testid="loan-cf-commit-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                {s.cashflows_to_add > 0 ? `Add ${s.cashflows_to_add} Cashflow(s)` : ''}
                {s.cashflows_to_add > 0 && s.column_corrections > 0 ? ' and ' : ''}
                {s.column_corrections > 0 ? `Correct ${s.column_corrections} column(s)` : ''}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent cashflow uploads</CardTitle>
        </CardHeader>
        <CardContent>
          {uploads.length === 0 ? (
            <p className="text-sm text-slate-500">No uploads yet.</p>
          ) : (
            <div className="space-y-2">
              {uploads.map(u => (
                <div key={u.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border rounded p-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.filename}</div>
                    <div className="text-xs text-slate-500">
                      {new Date(u.created_at).toLocaleString('en-ZA')} · {u.created_by} · {u.summary?.cashflows_added} added
                      {u.summary?.loans_created ? ` · ${u.summary.loans_created} new loan(s)` : ''}
                    </div>
                  </div>
                  {u.reversed ? (
                    <Badge variant="outline">Undone</Badge>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => undo(u)} data-testid={`loan-cf-undo-${u.id}`}>
                      <Undo2 className="h-4 w-4 mr-1" /> Undo
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Portal deals &amp; repayments missing from Reconciliation
            <InfoTip>
              New Deals and Repayments recorded in the portal before 6 Oct 2026 were written to the loan only, never
              to Reconciliation. This finds them and adds the missing Reconciliation entries. Anything already there
              (e.g. from a later workbook upload) is skipped, so it never doubles up. Safe to run more than once.
            </InfoTip>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button size="sm" variant="outline" onClick={checkBackfill} disabled={backfillBusy} data-testid="loan-cf-backfill-check">
            <RefreshCw className={`h-4 w-4 mr-1 ${backfillBusy ? 'animate-spin' : ''}`} /> Check
          </Button>
          {backfill && (
            <div className="text-sm space-y-2">
              {backfill.to_create === 0 ? (
                <p className="text-green-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Nothing missing — every portal deal and repayment is in Reconciliation.</p>
              ) : (
                <>
                  <p>
                    <strong>{backfill.to_create}</strong> cashflow(s) missing
                    ({backfill.by_kind.new_deal} from New Deals, {backfill.by_kind.repayment} from Repayments);
                    {' '}{backfill.already_in_reconciliation} already present.
                  </p>
                  <Button size="sm" onClick={runBackfill} disabled={backfillBusy} data-testid="loan-cf-backfill-run">
                    Add {backfill.to_create} to Reconciliation
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={!!undoPreview} onOpenChange={o => { if (!o) setUndoPreview(null); }}>
        <DialogContent className="flex flex-col w-screen max-w-none h-[100dvh] rounded-none p-3 sm:p-6 sm:w-full sm:max-w-3xl sm:h-auto sm:max-h-[85vh] sm:rounded-lg">
          <DialogHeader className="text-left pr-8">
            <DialogTitle>Undo upload and release matches?</DialogTitle>
            <DialogDescription>
              {undoPreview && <>
                "{undoPreview.filename}" added {undoPreview.cashflows} cashflow(s)
                {undoPreview.loans_created?.length ? ` and created ${undoPreview.loans_created.length} loan(s)` : ''}.{' '}
                <strong>{undoPreview.reconciled.length}</strong> of them are already matched or allocated. Undoing will release
                those matches — the bank transactions go back to unmatched so you can match them again.
              </>}
            </DialogDescription>
          </DialogHeader>
          {undoPreview && (
            <div className="flex-1 min-h-0 overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 text-left">
                  <tr><th className="p-2">Loan</th><th className="p-2">Side</th><th className="p-2">Date</th>
                    <th className="p-2 text-right">Amount</th><th className="p-2">Matched to</th></tr>
                </thead>
                <tbody>
                  {undoPreview.reconciled.map((c, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 font-mono text-xs whitespace-nowrap">{c.loan_id}</td>
                      <td className="p-2">{c.side} {c.owner}</td>
                      <td className="p-2 whitespace-nowrap">{c.date}</td>
                      <td className="p-2 text-right font-mono whitespace-nowrap">{fmt(c.amount)}</td>
                      <td className="p-2 text-xs">
                        {!c.bank ? (c.status || 'verified')
                          : c.bank.missing ? 'bank row no longer exists'
                          : <>{fmt(c.bank.amount)} · {c.bank.date} · {c.bank.description}</>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end border-t pt-3">
            <Button variant="outline" onClick={() => setUndoPreview(null)} disabled={undoBusy}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={undoBusy}
              onClick={() => runUndo(undoPreview.upload, true)}
              data-testid="loan-cf-undo-release-btn"
            >
              {undoBusy ? 'Working…' : `Undo and release ${undoPreview?.reconciled.length || 0} match(es)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LoanCashflowUploadCard;
